/**
 * RFC 0289 — the comment annotation write model.
 *
 * RFC 0339: a comment is written in the comments document of the page or
 * session it is about, then indexed on the project (`store.ts`). The
 * `projectId` each function takes is the project to index on when the page
 * does not name one, and the one whose leftovers are searched for a comment
 * written before the move.
 */
import type { DocHandle, Repo } from '@automerge/automerge-repo/slim'
import { v4 as uuidv4 } from 'uuid'
import type { ProjectDoc } from '../project/types'
import { usesPrivateSessionCatalog } from '../session/catalog'
import {
  type CommentHome,
  commentsDocFor,
  indexEntryOf,
  indexProjectFor,
  locateComment,
  subjectOf,
  writeCommentIndex,
} from './store'
import type {
  CommentEntry,
  CommentIndexEntry,
  CommentsDoc,
  CommentTarget,
  Mention,
} from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * The mentions to write, as PLAIN scalars, or undefined when there are none.
 *
 * Rebuilt field by field rather than spread, for the reason the target above
 * gives: a caller's object may carry keys whose value is `undefined` (an
 * idiomatic `id: picked?.id`), and Automerge throws on assigning one. A mention
 * missing its `kind` or `id` names nothing, so it is dropped here rather than
 * stored as a chip that resolves to a blank.
 */
function cleanMentions(mentions?: Mention[]): Mention[] | undefined {
  if (!mentions?.length) return undefined
  const out: Mention[] = []
  for (const m of mentions) {
    if (!m || typeof m !== 'object') continue
    if (m.kind !== 'session' && m.kind !== 'document') continue
    if (typeof m.id !== 'string' || !m.id) continue
    out.push({
      kind: m.kind,
      id: m.id,
      at: Number.isFinite(m.at) ? Number(m.at) : 0,
      len: Number.isFinite(m.len) ? Number(m.len) : 0,
    })
  }
  return out.length > 0 ? out : undefined
}

/**
 * Change the comments map a comment was found in, wherever that is. With
 * `every`, every copy of it: a comment the move copied into two comments
 * documents at once is one comment, and a resolve or a delete that reached
 * only one copy would leave the other showing.
 */
function changeHome(
  home: CommentHome,
  fn: (map: Draft) => void,
  opts: { every?: boolean } = {},
): void {
  const handles =
    home.kind === 'comments' && opts.every ? home.handles : [home.handle]
  for (const handle of handles) {
    ;(handle as DocHandle<CommentsDoc | ProjectDoc>).change((d: Draft) => {
      if (!d.comments) d.comments = {}
      fn(d.comments)
    })
  }
}

/**
 * Index what a change left in a comments document (3d). A comment still on the
 * project's leftovers is not indexed: the move indexes it when it moves it.
 *
 * On the page's own project, and on the caller's too when that is another
 * one, so a reply or a resolve by id from the project that wrote the comment
 * finds it there without searching the World.
 */
async function reindex(
  repo: Repo,
  projectId: string,
  home: CommentHome,
  ids: string[],
  removed: string[] = [],
): Promise<void> {
  if (home.kind !== 'comments' || usesPrivateSessionCatalog(repo)) return
  const map = home.handle.doc()?.comments ?? {}
  const entries: Record<string, CommentIndexEntry> = {}
  for (const id of ids) {
    const c = map[id]
    if (c) entries[id] = indexEntryOf(c, home.subject)
  }
  const indexOn = await indexProjectFor(repo, home.subject, projectId)
  await writeCommentIndex(repo, indexOn, entries, removed)
  if (indexOn !== projectId) {
    await writeCommentIndex(repo, projectId, entries, removed)
  }
}

/**
 * Create a comment (RFC 0267 &sect;4a), the core annotation interface. Anchored
 * to a `target` (a document, an app instance, a session or one of its
 * messages, and a diff hunk on a document), stored in the comments document
 * of the page or session the target names (RFC 0339 3a), then indexed on its
 * project. Returns the new comment id.
 *
 * THROWS on a target that names neither a document nor a session: a comment
 * needs something to live on.
 */
export async function createComment(
  repo: Repo,
  projectId: string,
  opts: {
    target: CommentTarget
    author: string
    body: string
    /** The session writing this, when an agent is (RFC 0289). Recorded so a
     *  later reply has somebody to wake. */
    authorSessionId?: string
    /** What the body NAMES (RFC 0292). Written in the SAME change as the body,
     *  so a reader never sees prose whose chips have not arrived yet. Waking a
     *  named session is a separate act: see `deliverMentions`. */
    mentions?: Mention[]
  },
): Promise<string> {
  const now = Date.now()
  const id = uuidv4()
  const mentions = cleanMentions(opts.mentions)
  // Keep only defined target fields. A spread ({...opts.target}) would PRESERVE
  // keys whose value is explicitly `undefined` (JS omits only never-assigned
  // keys), and Automerge throws "Cannot assign undefined value" on them — so a
  // caller passing `{ documentId, messageId: undefined }` (idiomatic
  // `messageId: selected?.id`) would blow up on an otherwise-valid target.
  const target: CommentTarget = {}
  for (const [k, v] of Object.entries(opts.target)) {
    if (v !== undefined) (target as Record<string, unknown>)[k] = v
  }
  const subject = subjectOf(target, projectId)
  if (!subject) {
    throw new Error(
      'A comment needs a documentId or a sessionId to live on (RFC 0339 3a)',
    )
  }
  const handle = await commentsDocFor(repo, subject)
  const home: CommentHome = {
    kind: 'comments',
    handle,
    handles: [handle],
    subject,
  }
  changeHome(home, (map) => {
    map[id] = {
      id,
      target,
      author: opts.author,
      body: opts.body,
      resolved: false,
      createdAt: now,
      updatedAt: now,
      ...(mentions ? { mentions } : {}),
      ...(opts.authorSessionId
        ? { authorSessionId: opts.authorSessionId }
        : {}),
    }
  })
  await reindex(repo, projectId, home, [id])
  return id
}

/**
 * Reply to a comment (RFC 0289 &sect;3). A reply is an ordinary `CommentEntry`
 * with two things done for it, which is the whole of threading:
 *
 * 1. `parentId` is NORMALISED TO THE ROOT. Replying to a reply attaches to that
 *    reply's parent, so a thread is always a root plus a flat list. One level is
 *    what every review tool worth copying does, and depth buys ambiguity in the
 *    resolve rule for nothing.
 * 2. The root's `target` is COPIED onto the reply. `projectComments` filters by
 *    subset match over `target`, so a reply with no target of its own would be
 *    invisible to every reader that predates threading.
 *
 * Written where the parent is, so a thread is never split between a comments
 * document and the project's leftovers.
 *
 * THROWS on an unknown parent, unlike `resolveComment`, which is a deliberate
 * no-op. Resolving a comment that is already gone costs nothing; a reply that
 * silently evaporates costs the text somebody typed.
 *
 * Returns the new comment id.
 */
export async function replyToComment(
  repo: Repo,
  projectId: string,
  opts: {
    parentId: string
    author: string
    body: string
    /** The session writing the reply, when an agent is (RFC 0289). Recorded
     *  so a further reply in this thread can wake it back. NOT inherited from
     *  the root: this is who wrote THIS comment, and a person's reply in an
     *  agent's thread must not claim the agent's session or the notification
     *  would be addressed back to the agent that is waiting for it. */
    authorSessionId?: string
    /** What the reply NAMES (RFC 0292). NOT inherited from the root, for the
     *  same reason `authorSessionId` is not: these are the things THIS text
     *  points at. */
    mentions?: Mention[]
  },
): Promise<string> {
  const now = Date.now()
  const id = uuidv4()
  const mentions = cleanMentions(opts.mentions)
  const home = await locateComment(repo, projectId, opts.parentId)
  if (!home) throw new Error(`no comment ${opts.parentId} to reply to`)
  let rootId = opts.parentId
  changeHome(home, (map) => {
    const parent = map[opts.parentId]
    // One level: the parent's parent is the root, or the parent is.
    // As a plain string, for the same reason the target below is rebuilt: a
    // value read off a document can be an Automerge wrapper, and storing one
    // here would put it in every reader's way.
    rootId = String(parent.parentId ?? parent.id)
    const root = map[rootId] ?? parent
    // Rebuild the target as plain strings rather than assigning the parent's
    // Automerge proxy into a new entry.
    const target: CommentTarget = {}
    for (const [k, v] of Object.entries(root.target ?? {})) {
      if (v) (target as Record<string, string>)[k] = String(v)
    }
    map[id] = {
      id,
      target,
      author: opts.author,
      body: opts.body,
      resolved: false,
      createdAt: now,
      updatedAt: now,
      parentId: rootId,
      ...(mentions ? { mentions } : {}),
      ...(opts.authorSessionId
        ? { authorSessionId: opts.authorSessionId }
        : {}),
    }
  })
  await reindex(repo, projectId, home, [id])
  return id
}

/**
 * Resolve a comment (RFC 0267 &sect;4a): mark it done, bumping `updatedAt`. The
 * counterpart the review canvas rolls into a verdict. A no-op if the id is
 * unknown (idempotent, and safe against a stale client).
 */
export async function resolveComment(
  repo: Repo,
  projectId: string,
  commentId: string,
  resolved = true,
): Promise<void> {
  const now = Date.now()
  const home = await locateComment(repo, projectId, commentId)
  if (!home) return
  let rootId = commentId
  changeHome(
    home,
    (map) => {
      const c = map[commentId]
      if (!c) return
      // Resolve is a property of the THREAD, read from its root (RFC 0289 §3), so
      // resolving a reply resolves the conversation it belongs to. Otherwise
      // "resolve this" would mean something different depending on which message
      // in a thread the caller happened to be looking at.
      const root = (c.parentId ? map[c.parentId] : undefined) ?? c
      root.resolved = resolved
      root.updatedAt = now
      rootId = String(root.id)
    },
    { every: true },
  )
  await reindex(repo, projectId, home, [rootId])
}

/**
 * Record that a session read a comment and chose not to answer (RFC 0324 3g).
 * A no-op if the id is unknown. Not indexed: no list reads it.
 */
export async function markCommentRead(
  repo: Repo,
  projectId: string,
  commentId: string,
  sessionId: string,
): Promise<void> {
  const home = await locateComment(repo, projectId, commentId)
  if (!home) return
  changeHome(
    home,
    (map) => {
      const c = map[commentId]
      if (!c) return
      if (!c.readBy) c.readBy = {}
      c.readBy[sessionId] = Date.now()
    },
    { every: true },
  )
}

/**
 * Delete a comment (RFC 0267 &sect;4a). A no-op if the id is unknown, like
 * resolve.
 *
 * SEPARATE FROM RESOLVE, because they mean different things and only one of
 * them is reversible. Resolving says "this was dealt with" and keeps the
 * exchange readable; deleting says "this should never have been said" and takes
 * the text with it. A reviewer who typed into the wrong file needs the second
 * one, and resolving is not a substitute: the comment stays on the line,
 * greyed, still anchored to code it was never about.
 *
 * Deleting a ROOT takes its replies with it (RFC 0289 &sect;11). They inherit
 * its target and answer text that is gone, and `projectThreads` PROMOTES a reply
 * whose parent it cannot see, so leaving them behind would not orphan them
 * quietly — it would turn half an exchange into top-level comments that read as
 * though they were about the document itself. Promotion exists to survive a
 * root that has not synced YET; a deleted root is never arriving.
 *
 * Deleting a REPLY takes only that reply.
 */
export async function deleteComment(
  repo: Repo,
  projectId: string,
  commentId: string,
): Promise<void> {
  const home = await locateComment(repo, projectId, commentId)
  if (!home) return
  const removed: string[] = []
  changeHome(
    home,
    (map) => {
      const target = map[commentId] as CommentEntry | undefined
      // Unknown id is a no-op, like resolve: a stale client must not be able to
      // turn a missed delete into a thrown error the user cannot act on.
      if (!target) return
      if (!target.parentId) {
        for (const other of Object.values(map) as CommentEntry[]) {
          if (other.parentId === commentId) {
            removed.push(String(other.id))
            delete map[other.id]
          }
        }
      }
      removed.push(commentId)
      delete map[commentId]
    },
    { every: true },
  )
  await reindex(repo, projectId, home, [], [...new Set(removed)])
}
